import { useCallback, useEffect, useRef, useState } from 'react';
import { formatFileSize } from '@brimo/shared';
import { errorMessage } from '../../../lib/api';
import { uploadWizardFile } from '../api';

export type LocalUploadStatus = 'queued' | 'uploading' | 'done' | 'error';

export interface LocalUpload {
  id: string;
  file: File;
  name: string;
  sizeLabel: string;
  kind: string;
  status: LocalUploadStatus;
  /** 0–100 while uploading. */
  progress: number;
  error: string | null;
  documentId: string | null;
}

const CONCURRENCY = 3;

function kindOf(name: string): string {
  const dot = name.lastIndexOf('.');
  return dot >= 0
    ? name
        .slice(dot + 1)
        .slice(0, 4)
        .toLowerCase()
    : 'file';
}

let counter = 0;

/**
 * The bulk upload of Objekt anlegen 02: one `POST /properties/:id/documents` per file with progress
 * events, three at a time, the rest „in Warteschlange". Lives in the wizard page so that uploads keep
 * running while the user moves on („Hochladen läuft im Hintergrund weiter").
 */
export function useUploadQueue(propertyId: string | null, onUploaded: () => void) {
  const listRef = useRef<LocalUpload[]>([]);
  const runningRef = useRef(0);
  const mountedRef = useRef(true);
  const [items, setItems] = useState<LocalUpload[]>([]);
  const onUploadedRef = useRef(onUploaded);
  onUploadedRef.current = onUploaded;

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const publish = useCallback(() => {
    if (mountedRef.current) setItems([...listRef.current]);
  }, []);

  const patch = useCallback(
    (id: string, changes: Partial<LocalUpload>) => {
      listRef.current = listRef.current.map((item) => (item.id === id ? { ...item, ...changes } : item));
      publish();
    },
    [publish],
  );

  const pump = useCallback(() => {
    if (propertyId === null) return;
    while (runningRef.current < CONCURRENCY) {
      const next = listRef.current.find((item) => item.status === 'queued');
      if (!next) return;
      runningRef.current += 1;
      patch(next.id, { status: 'uploading', progress: 0, error: null });
      void uploadWizardFile(propertyId, next.file, {
        onProgress: (fraction) => patch(next.id, { progress: Math.min(99, Math.round(fraction * 100)) }),
      })
        .then((response) => {
          const result = response.items[0];
          if (result?.ok && result.documentId) {
            patch(next.id, { status: 'done', progress: 100, documentId: result.documentId });
          } else {
            patch(next.id, {
              status: 'error',
              error: result?.error ?? 'Die Datei konnte nicht hochgeladen werden.',
            });
          }
        })
        .catch((error: unknown) => {
          patch(next.id, { status: 'error', error: errorMessage(error) });
        })
        .finally(() => {
          runningRef.current -= 1;
          onUploadedRef.current();
          pump();
        });
    }
  }, [patch, propertyId]);

  const add = useCallback(
    (files: File[]) => {
      const entries: LocalUpload[] = files.map((file) => ({
        id: `local-${Date.now()}-${counter++}`,
        file,
        name: file.name,
        sizeLabel: formatFileSize(file.size),
        kind: kindOf(file.name),
        status: 'queued',
        progress: 0,
        error: null,
        documentId: null,
      }));
      listRef.current = [...listRef.current, ...entries];
      publish();
      pump();
    },
    [publish, pump],
  );

  const retry = useCallback(
    (id: string) => {
      patch(id, { status: 'queued', progress: 0, error: null });
      pump();
    },
    [patch, pump],
  );

  const remove = useCallback(
    (id: string) => {
      listRef.current = listRef.current.filter((item) => item.id !== id);
      publish();
    },
    [publish],
  );

  /** Drops finished local rows once the wizard document lists their documents. */
  const reconcile = useCallback(
    (knownDocumentIds: ReadonlySet<string>) => {
      const before = listRef.current.length;
      listRef.current = listRef.current.filter(
        (item) =>
          !(item.status === 'done' && item.documentId !== null && knownDocumentIds.has(item.documentId)),
      );
      if (listRef.current.length !== before) publish();
    },
    [publish],
  );

  return { items, add, retry, remove, reconcile };
}
